import React, { ChangeEvent, PropsWithChildren, ReactNode } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useSearchAutocomplete } from "Hooks";
import { isPlural } from "Utils/isPlural";
import { Button } from "Components/ReactstrapSlim";
import { TypeaheadFL, TypeaheadFLProps } from "Components/Inputs/TypeaheadFL";
import * as Icons from "Components/Icons";
import classNames from "classnames";
import { Menu, MenuItem, TypeaheadResult, TypeaheadMenuProps } from "react-bootstrap-typeahead";
import { createPortal } from "react-dom";
import exhaustiveCheck from "Utils/exhaustiveCheck";
import { CollectionSearchAutocomplete, GeoSearchAutocomplete, PlacePredictionViewModel, SavedSearchRequestAutocomplete, VenueGroupSearchAutocomplete, VenueSearchAutocomplete } from "Services/API/API";
import { FullTextSearchAutocomplete, KnownSearchAutocomplete } from "Models/Search";

type AutocompleteData = KnownSearchAutocomplete | PlacePredictionViewModel;

export const renderAutocompleteMenu = (results: Array<TypeaheadResult<AutocompleteData>>, menuProps: TypeaheadMenuProps<AutocompleteData>, onEditHomeBase?: (savedSearchId: Guid) => void) => {
   return (<Menu {...menuProps} className={classNames(menuProps.className, "search-autocomplete")}>
      {results.map((result, index) => (
         <MenuItem key={index} option={result} position={index} className="search-autocomplete-item">
            {renderAutocompleteMenuItem(result, onEditHomeBase)}
         </MenuItem>
      ))}
   </Menu>)
}

export const renderAutocompleteMenuItem = (option: AutocompleteData, onEditHomeBase?: (savedSearchId: Guid) => void): ReactNode => {
   let optionLabel: string;
   let icon: JSX.Element;
   let extraInfo: React.ReactNode | undefined;

   if (option instanceof VenueSearchAutocomplete) {
      optionLabel = option.venueName;
      icon = <Icons.LocationOn />
   }
   else if (option instanceof VenueGroupSearchAutocomplete) {
      optionLabel = option.venueGroupName;
      icon = option.iconUrl
         ? <img src={option.iconUrl} alt={option.venueGroupName} />
         : option.isPreferred
            ? <Icons.Preferred />
            : <Icons.OfficeBuilding />;
      extraInfo = `(${option.venuesCount} ${isPlural(option.venuesCount) ? "locations" : "location"})`
   }
   else if (option instanceof SavedSearchRequestAutocomplete) {
      optionLabel = `Home base - ${option.predefinedText}`;
      icon = <Icons.Flag />
      if (onEditHomeBase) {
         extraInfo = <Button color="arctic-20" size="sm" className="search-autocomplete-item-button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEditHomeBase(option.savedSearchId); }}>Edit</Button>
      }
   }
   else if (option instanceof GeoSearchAutocomplete) {
      optionLabel = option.geoQuery;
      icon = <Icons.Find />
   }
   else if (option instanceof CollectionSearchAutocomplete) {
      optionLabel = option.collectionName;
      icon = <Icons.Like />
      extraInfo = `(${option.favoritesCount} ${isPlural(option.favoritesCount) ? "spaces" : "space"})`;
   }
   else if (option instanceof FullTextSearchAutocomplete) {
      optionLabel = `${option.fullTextQuery} - Search`;
      icon = <Icons.Find />
   } else if (option instanceof PlacePredictionViewModel) {
      optionLabel = option.text ?? "";
      icon = <Icons.Find />
   }
   else {
      exhaustiveCheck(option);
      icon = <Icons.Find />
      optionLabel = ""
   }

   return (<>
      <div className="search-autocomplete-item-icon">{icon}</div>
      <div className="search-autocomplete-item-text">{optionLabel}</div>
      <div className="search-autocomplete-item-info">{extraInfo}</div>
   </>);
}

export interface SearchAutocompletePropsNoIcon<T extends FieldValues> {
   control: Control<T>;
   name: FieldPath<T>;
   onItemSelected: (selectedOption: KnownSearchAutocomplete) => void;
   onInputChange?: (value: string) => void;
   onInputSubmit?: (value: string) => void;
   size?: TypeaheadFLProps<KnownSearchAutocomplete>["size"];
   placeholder?: string;
   onEditHomeBase?: (savedSearchId: Guid) => void;
   //inputValue is used to set input value programmatically... See https://stackoverflow.com/questions/66170215/update-input-text-in-react-bootstrap-typeahead-from-javascript-react
   //Also, typeahead issue thread https://github.com/ericgio/react-bootstrap-typeahead/issues/266
   inputValue?: string;
}

export interface SearchAutocompleteProps<T extends FieldValues> extends SearchAutocompletePropsNoIcon<T> {
   icon: TypeaheadFLProps<KnownSearchAutocomplete>["icon"];
}

function getLabelKey(option?: KnownSearchAutocomplete): string {
   if (option instanceof VenueSearchAutocomplete) {
      return option.venueName;
   }
   else if (option instanceof VenueGroupSearchAutocomplete) {
      return option.venueGroupName;
   }
   else if (option instanceof SavedSearchRequestAutocomplete) {
      return option.predefinedText ?? "";
   }
   else if (option instanceof GeoSearchAutocomplete) {
      return option.geoQuery;
   }
   else if (option instanceof CollectionSearchAutocomplete) {
      return option.collectionName;
   }
   else if (option instanceof FullTextSearchAutocomplete) {
      return option.fullTextQuery;
   }
   else if (option == undefined) {
      return "";
   }
   else {
      exhaustiveCheck(option);
      return "";
   }
}

export const SearchAutocomplete = function <T extends FieldValues>({ control, name, icon, size, placeholder, onEditHomeBase, onItemSelected, onInputSubmit, onInputChange, inputValue }: PropsWithChildren<SearchAutocompleteProps<T>>) {
   const { isLoading, options, handleSearch } = useSearchAutocomplete();


   return (
      <Controller control={control} name={name} render={({ field, fieldState }) =>
         <TypeaheadFL
            id={field.name}
            icon={icon}
            filterBy={() => true}
            isLoading={isLoading}
            labelKey={option => getLabelKey(option)}
            minLength={3}
            onSearch={handleSearch}
            options={options}
            placeholder={placeholder ?? "Search workspace"}
            onChange={selectedOptions => {
               if (onItemSelected)
                  onItemSelected(selectedOptions[0]);
            }}
            onInputChange={(s, ev) => {
               if (onInputChange)
                  onInputChange(s);
               field.onChange(ev as unknown as ChangeEvent<Element>);
            }}
            onBlur={() => field.onBlur()}
            size={size}
            paginate
            renderMenu={(results, menuProps) => createPortal((renderAutocompleteMenu(results, menuProps, onEditHomeBase)), document.body)}
            onInputSubmit={onInputSubmit}
            inputValue={inputValue ?? ""}
         />
      } />
   );
}